import { projectFilesStorageBase } from './supabase';

const API_BASE = 'https://app.hikari.now';

export function storageUrl(path: string): string {
  if (path.startsWith('http')) return path;
  return `${projectFilesStorageBase}/${path}`;
}

export interface AgencyProfile {
  user_id: string;
  username: string;
  email: string;
  avatar_url: string | null;
  role_title: string | null;
  created_at: string;
}

export interface UserInfo {
  id: string;
  username: string;
  user_type: string;
}

export interface AuthData {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: UserInfo;
}

export interface ClientProfile {
  user_id: string;
  username: string;
  email: string;
  full_name: string;
  phone: string | null;
  created_at: string;
}

export interface Project {
  id: string;
  client_id: string;
  name: string;
  description: string | null;
  start_date: string | null;
  deadline: string | null;
  current_phase: string | null;
  progress: number;
  status: 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED' | 'UPCOMING' | 'MAINTENANCE';
  created_at: string;
  updated_at: string;
}

export interface Milestone {
  project_id: string;
  milestone: 'DESIGN' | 'FRONTEND' | 'BACKEND' | 'TESTING' | 'DEPLOYMENT' | 'LAUNCH';
  due_date: string | null;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
  progress: number;
}

export type MilestoneType = Milestone['milestone'];

export interface Activity {
  id: string;
  project_id: string;
  milestone: MilestoneType | null;
  created_by: string;
  title: string;
  description: string | null;
  change_percentage: number | null;
  result_percentage: number | null;
  scheduled_at: string | null;
  published_at: string | null;
  status: 'DRAFT' | 'SCHEDULED' | 'PUBLISHED';
  created_at: string;
}

export interface Design {
  id: string;
  project_id: string;
  uploaded_by: string;
  title: string;
  description: string | null;
  file_urls: string[];
  figma_url: string;
  version: number;
  status: 'PENDING_REVIEW' | 'APPROVED' | 'REVISION_REQUESTED' | 'REJECTED';
  review_comment: string | null;
  change_made_comment: string | null;
  feedback_led_to_change_comment: string[] | null;
  reviewed_at: string | null;
  created_at: string;
}

export type ResourceType = 'IMAGE' | 'VIDEO' | 'DOC' | 'LINK';
export type ResourceTag = 'DESIGN' | 'GENERAL' | 'DEV';

export interface Resource {
  id: string;
  project_id: string;
  uploaded_by: string;
  name: string;
  type: ResourceType;
  urls: string[];
  tag: ResourceTag | null;
  created_at: string;
}

export interface UploadURLData {
  upload_url: string;
  path: string;
}

export interface Meeting {
  id: string;
  project_id: string;
  created_by: string;
  title: string;
  notes: string | null;
  agenda: string | null;
  tldr: string | null;
  scheduled_at: string;
  duration_minutes: number;
  meet_link: string | null;
  status: 'SCHEDULED' | 'COMPLETED';
  recurrence_rule: string | null;
  recurrence_end_date: string | null;
  parent_meeting_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface MeetingResponse {
  success: boolean;
  data: Meeting;
}

export interface MeetingListResponse {
  success: boolean;
  data: Meeting[];
}

export interface CreateMeetingRequest {
  title: string;
  scheduled_at: string;
  duration_minutes: number;
  notes?: string;
  agenda?: string;
  recurrence_rule?: string;
  recurrence_end_date?: string;
}

export interface UpdateMeetingRequest {
  title?: string;
  scheduled_at?: string;
  duration_minutes?: number;
  notes?: string;
  agenda?: string;
}

export interface GoogleOAuthStatusResponse {
  success: boolean;
  data: { connected: boolean; email: string | null };
}

function getAccessToken(): string | null {
  return sessionStorage.getItem('access_token');
}

function getRefreshToken(): string | null {
  return sessionStorage.getItem('refresh_token');
}

export function setTokens(accessToken: string, refreshToken: string): void {
  sessionStorage.setItem('access_token', accessToken);
  sessionStorage.setItem('refresh_token', refreshToken);
}

export function clearTokens(): void {
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('refresh_token');
  sessionStorage.removeItem('user');
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) { clearTokens(); return null; }
    const data = await res.json();
    const { access_token, refresh_token } = data.data as AuthData;
    setTokens(access_token, refresh_token);
    return access_token;
  } catch {
    clearTokens();
    return null;
  }
}

async function request<T>(path: string, options: RequestInit = {}, retry = true): Promise<T> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 401 && retry) {
    const newToken = await refreshAccessToken();
    if (newToken) return request<T>(path, options, false);
    clearTokens();
    // Defer the redirect so callers can catch and display the error first
    setTimeout(() => window.dispatchEvent(new Event('auth:unauthorized')), 100);
    throw new Error('Session expired. Please log in again.');
  }

  const data = await res.json();
  if (!res.ok) throw new Error(data.error?.message || data.message || 'Request failed');
  return data as T;
}

export const authApi = {
  sendOtp: (username: string) =>
    request<{ success: boolean; message: string }>('/api/v1/auth/otp/send', {
      method: 'POST',
      body: JSON.stringify({ username }),
    }),

  verifyOtp: (username: string, token: string) =>
    request<{ success: boolean; data: AuthData }>('/api/v1/auth/otp/verify', {
      method: 'POST',
      body: JSON.stringify({ username, token }),
    }),

  logout: () =>
    request<{ success: boolean; message: string }>('/api/v1/auth/logout', {
      method: 'POST',
    }),
};

export const agencyApi = {
  getMe: () =>
    request<{ success: boolean; data: AgencyProfile }>('/api/v1/agency/me'),

  updateMe: (data: { avatar_url?: string | null; role_title?: string | null }) =>
    request<{ success: boolean; data: AgencyProfile }>('/api/v1/agency/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  getTeam: () =>
    request<{ success: boolean; data: AgencyProfile[] }>('/api/v1/agency/team'),

  inviteMember: (data: { username: string; email: string; full_name: string; role_title?: string }) =>
    request<{ success: boolean; data: AgencyProfile }>('/api/v1/agency/team/invite', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  removeMember: (userId: string) =>
    request<{ success: boolean; message: string }>(`/api/v1/agency/team/${userId}`, {
      method: 'DELETE',
    }),
};

export const clientApi = {
  getAll: () =>
    request<{ success: boolean; data: ClientProfile[] }>('/api/v1/clients'),

  get: (clientId: string) =>
    request<{ success: boolean; data: ClientProfile }>(`/api/v1/clients/${clientId}`),

  create: (data: { username: string; email: string; full_name: string; phone?: string }) =>
    request<{ success: boolean; data: ClientProfile }>('/api/v1/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (clientId: string, data: { username?: string; email?: string; full_name?: string; phone?: string | null }) =>
    request<{ success: boolean; data: ClientProfile }>(`/api/v1/clients/${clientId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (clientId: string) =>
    request<{ success: boolean; message: string }>(`/api/v1/clients/${clientId}`, {
      method: 'DELETE',
    }),
};

export const projectApi = {
  getAll: () =>
    request<{ success: boolean; data: Project[] }>('/api/v1/projects'),

  get: (projectId: string) =>
    request<{ success: boolean; data: Project }>(`/api/v1/projects/${projectId}`),

  create: (data: { client_id: string; name: string; description?: string; start_date?: string; deadline?: string; current_phase?: string }) =>
    request<{ success: boolean; data: Project }>('/api/v1/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (projectId: string, data: { name?: string; description?: string | null; start_date?: string | null; deadline?: string | null; current_phase?: string | null; progress?: number; status?: Project['status'] }) =>
    request<{ success: boolean; data: Project }>(`/api/v1/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (projectId: string) =>
    request<{ success: boolean; message: string }>(`/api/v1/projects/${projectId}`, {
      method: 'DELETE',
    }),

  getMilestones: (projectId: string) =>
    request<{ success: boolean; data: Milestone[] }>(`/api/v1/projects/${projectId}/milestones`),

  updateMilestone: (projectId: string, milestoneType: string, data: { due_date?: string | null; progress?: number }) =>
    request<{ success: boolean; data: Milestone }>(`/api/v1/projects/${projectId}/milestones/${milestoneType}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
};

export const activityApi = {
  getAll: (projectId: string) =>
    request<{ success: boolean; data: Activity[] }>(`/api/v1/projects/${projectId}/activities`),

  get: (projectId: string, activityId: string) =>
    request<{ success: boolean; data: Activity }>(`/api/v1/projects/${projectId}/activities/${activityId}`),

  create: (projectId: string, data: { title: string; description?: string; milestone?: MilestoneType; change_percentage?: number; scheduled_at?: string }) =>
    request<{ success: boolean; data: Activity }>(`/api/v1/projects/${projectId}/activities`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (projectId: string, activityId: string, data: { title?: string; description?: string | null; milestone?: MilestoneType | null; change_percentage?: number | null; scheduled_at?: string | null }) =>
    request<{ success: boolean; data: Activity }>(`/api/v1/projects/${projectId}/activities/${activityId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (projectId: string, activityId: string) =>
    request<{ success: boolean; message: string }>(`/api/v1/projects/${projectId}/activities/${activityId}`, {
      method: 'DELETE',
    }),
};

export const designApi = {
  getAll: (projectId: string) =>
    request<{ success: boolean; data: Design[] }>(`/api/v1/projects/${projectId}/designs`),

  get: (projectId: string, designId: string) =>
    request<{ success: boolean; data: Design }>(`/api/v1/projects/${projectId}/designs/${designId}`),

  create: (projectId: string, data: { title: string; file_urls: string[]; figma_url: string; description?: string; change_made_comment?: string; feedback_led_to_change_comment?: string[] }) =>
    request<{ success: boolean; data: Design }>(`/api/v1/projects/${projectId}/designs`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (projectId: string, designId: string, data: { description?: string; status?: Design['status'] }) =>
    request<{ success: boolean; data: Design }>(`/api/v1/projects/${projectId}/designs/${designId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (projectId: string, designId: string) =>
    request<{ success: boolean; message: string }>(`/api/v1/projects/${projectId}/designs/${designId}`, {
      method: 'DELETE',
    }),

  updateFigmaUrl: (projectId: string, designId: string, data: { figma_url: string }) =>
    request<{ success: boolean; data: Design }>(`/api/v1/projects/${projectId}/designs/${designId}/figma-url`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  getUploadUrl: (projectId: string, data: { filename: string; content_type: string }) =>
    request<{ success: boolean; data: UploadURLData }>(`/api/v1/projects/${projectId}/designs/upload-url`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

export const resourceApi = {
  getAll: (projectId: string, type?: ResourceType, tag?: ResourceTag) => {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (tag) params.set('tag', tag);
    const qs = params.toString();
    return request<{ success: boolean; data: Resource[] }>(`/api/v1/projects/${projectId}/resources${qs ? `?${qs}` : ''}`);
  },

  get: (projectId: string, resourceId: string) =>
    request<{ success: boolean; data: Resource }>(`/api/v1/projects/${projectId}/resources/${resourceId}`),

  create: (projectId: string, data: { name: string; type: ResourceType; urls: string[]; tag?: ResourceTag }) =>
    request<{ success: boolean; data: Resource }>(`/api/v1/projects/${projectId}/resources`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (projectId: string, resourceId: string, data: { name?: string; tag?: ResourceTag | null }) =>
    request<{ success: boolean; data: Resource }>(`/api/v1/projects/${projectId}/resources/${resourceId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (projectId: string, resourceId: string) =>
    request<{ success: boolean; message: string }>(`/api/v1/projects/${projectId}/resources/${resourceId}`, {
      method: 'DELETE',
    }),

  getUploadUrl: (projectId: string, data: { filename: string }) =>
    request<{ success: boolean; data: UploadURLData }>(`/api/v1/projects/${projectId}/resources/upload-url`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

export const meetingApi = {
  list: (projectId: string) =>
    request<MeetingListResponse>(`/api/v1/projects/${projectId}/meetings`),

  listAll: (params?: {
    project_ids?: string;
    status?: string;
    tldr_pending?: boolean;
    from_date?: string;
    to_date?: string;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.project_ids) qs.set('project_ids', params.project_ids);
    if (params?.status) qs.set('status', params.status);
    if (params?.tldr_pending) qs.set('tldr_pending', 'true');
    if (params?.from_date) qs.set('from_date', params.from_date);
    if (params?.to_date) qs.set('to_date', params.to_date);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const q = qs.toString();
    return request<MeetingListResponse>(`/api/v1/meetings${q ? '?' + q : ''}`);
  },

  get: (projectId: string, meetingId: string) =>
    request<MeetingResponse>(`/api/v1/projects/${projectId}/meetings/${meetingId}`),

  create: (projectId: string, data: CreateMeetingRequest) =>
    request<MeetingListResponse>(`/api/v1/projects/${projectId}/meetings`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    }),

  update: (projectId: string, meetingId: string, data: UpdateMeetingRequest) =>
    request<MeetingResponse>(`/api/v1/projects/${projectId}/meetings/${meetingId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    }),

  delete: (projectId: string, meetingId: string) =>
    request<{ success: boolean; message: string }>(
      `/api/v1/projects/${projectId}/meetings/${meetingId}`,
      { method: 'DELETE' },
    ),

  setTldr: (projectId: string, meetingId: string, tldr: string) =>
    request<MeetingResponse>(
      `/api/v1/projects/${projectId}/meetings/${meetingId}/tldr`,
      {
        method: 'PATCH',
        body: JSON.stringify({ tldr }),
        headers: { 'Content-Type': 'application/json' },
      },
    ),
};

export const googleOAuth = {
  status: () =>
    request<GoogleOAuthStatusResponse>('/api/v1/google/status'),

  disconnect: () =>
    request<{ success: boolean; message: string }>('/api/v1/google/disconnect', {
      method: 'DELETE',
    }),

  connectUrl: `${API_BASE}/api/v1/google/connect`,
};

export interface ChatMessage {
  id: string;
  project_id: string;
  sender_id: string;
  sender_name: string;
  sender_type: 'agency' | 'client';
  content: string;
  is_internal?: boolean;
  read_at: string | null;
  created_at: string;
}

export interface MessageListData {
  messages: ChatMessage[];
  next_cursor?: string;
  has_more: boolean;
}

export const messageApi = {
  list: (projectId: string, cursor?: string, limit?: number) => {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    return request<{ success: boolean; data: MessageListData }>(
      `/api/v1/projects/${projectId}/messages${qs ? `?${qs}` : ''}`
    );
  },
  send: (projectId: string, data: { content: string; is_internal?: boolean }) =>
    request<{ success: boolean; data: ChatMessage }>(`/api/v1/projects/${projectId}/messages`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  delete: (projectId: string, messageId: string) =>
    request<{ success: boolean; data: { message: string } }>(`/api/v1/projects/${projectId}/messages/${messageId}`, {
      method: 'DELETE',
    }),
  markRead: (projectId: string) =>
    request<{ success: boolean; data: { message: string } }>(`/api/v1/projects/${projectId}/messages/read`, {
      method: 'PATCH',
    }),
  unreadCounts: () =>
    request<{ success: boolean; data: { counts: Record<string, number> } }>('/api/v1/messages/unread-counts'),
};

export interface Notification {
  id: string;
  recipient_id: string;
  type: string;
  title: string;
  body: string;
  entity_type?: string | null;
  entity_id?: string | null;
  project_id?: string | null;
  actor_id?: string | null;
  read_at: string | null;
  created_at: string;
}

export interface NotificationListData {
  notifications: Notification[];
  next_cursor?: string;
  has_more: boolean;
}

export interface PushPreference {
  design_uploaded: boolean;
  design_reviewed: boolean;
  activity_published: boolean;
  milestone_complete: boolean;
  new_message: boolean;
  resource_uploaded: boolean;
}

export const notificationApi = {
  list: (cursor?: string, limit?: number) => {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    return request<{ success: boolean; data: NotificationListData }>(
      `/api/v1/notifications${qs ? `?${qs}` : ''}`
    );
  },

  unreadCount: () =>
    request<{ success: boolean; data: { count: number } }>('/api/v1/notifications/unread-count'),

  markRead: (id: string) =>
    request<{ success: boolean; data: Notification }>(`/api/v1/notifications/${id}/read`, {
      method: 'PATCH',
    }),

  markAllRead: () =>
    request<{ success: boolean; message: string }>('/api/v1/notifications/read-all', {
      method: 'PATCH',
    }),
};

export const pushPreferencesApi = {
  get: (userId?: string) => {
    const qs = userId ? `?user_id=${userId}` : '';
    return request<{ success: boolean; data: PushPreference }>(
      `/api/v1/push-preferences${qs}`
    );
  },

  update: (prefs: Partial<PushPreference>, userId?: string) => {
    const qs = userId ? `?user_id=${userId}` : '';
    return request<{ success: boolean; data: PushPreference }>(
      `/api/v1/push-preferences${qs}`,
      { method: 'PATCH', body: JSON.stringify(prefs) }
    );
  },
};

export async function uploadToSignedUrl(uploadUrl: string, file: File): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!res.ok) throw new Error('Upload failed');
}
